import { useStripe, useElements } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../store/selectors';
import { PaymentDialog, PriceItem } from '../PaymentDialog';
import { LateFeeType } from '../../../wallet/models/LateFeeType';
import createNotification from '../../../utils/createNotification';

interface LateFeePaymentComponentProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    paymentSuccess: (transactionId: string) => void;
    engagementId: number;
    eventId: string;
    extensionDays: number;
    fteDvms: number;
    feeAmount: number;
    totalPrice: number;
    firstOccurancePrice: number;
    lateFeeType: LateFeeType;
}

export interface LateFeePaymentDetails {
    lateDays: number;
    fteDvms: number;
    feeAmount: number;
    totalPrice: number;
    firstOccurancePrice: number;
    eventId: string;
    lateFeeType: LateFeeType;
}

const LateFeePaymentComponent = (props: LateFeePaymentComponentProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const stripe = useStripe();
    const elements = useElements();

    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);

    useEffect(() => {
        setOriginalServicePrice(props.totalPrice);
    }, []);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    const getLateFeeLabel = () => {
        switch (props.lateFeeType) {
            case LateFeeType.MissedDeadline:
                return 'Late Event Fee';
            case LateFeeType.DeadlineExtension:
                return 'Event Deadline Extension Fee';
            default:
                return 'Late Fee';
        }
    };

    const getDaysLateLabel = () => {
        switch (props.lateFeeType) {
            case LateFeeType.MissedDeadline:
                return 'Late Days:';
            case LateFeeType.DeadlineExtension:
                return 'Extended Days:';
            default:
                return 'Days:';
        }
    };

    const getFeeNameLabel = () => {
        switch (props.lateFeeType) {
            case LateFeeType.MissedDeadline:
                return 'Late Fee:';
            case LateFeeType.DeadlineExtension:
                return 'Extension Fee:';
            default:
                return 'Fee Amount:';
        }
    };

    const priceList: PriceItem[] = [
        {
            priceType: 'summary',
            label: getLateFeeLabel(),
        },
        ...(!!props.firstOccurancePrice
            ? [
                  {
                      priceType: 'regular',
                      label: 'First Occurance Fee:',
                      value: props.firstOccurancePrice,
                      valueType: 'currency',
                  } as PriceItem,
              ]
            : []),
        ...(props.extensionDays > 1
            ? [
                  {
                      priceType: 'regular',
                      label: getFeeNameLabel(),
                      value: props.feeAmount,
                      valueType: 'currency',
                  } as PriceItem,
              ]
            : []),

        {
            priceType: 'regular',
            label: getDaysLateLabel(),
            value: props.extensionDays,
            valueType: 'number',
        },
        {
            priceType: 'regular',
            label: 'Seller FTE DVMs:',
            value: props.fteDvms,
            valueType: 'number',
        },
        {
            priceType: 'summary',
            label: 'Total Due:',
            value: props.totalPrice,
            valueType: 'currency',
        },
    ];

    const directPayment = () => payLateFee(false);
    const payLateFee = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        axios
            .post('/api/MarketplaceCommerce/DeadlineExtensionPurchase', {
                walletId: 0,
                engagementId: props.engagementId,
                eventId: props.eventId,
                useWalletBalance: !!appliedCredits,
                walletAmount: appliedCredits,
                lateFeeType: props.lateFeeType,
                useSavedPaymentMethod: shouldUseSavedPaymentMethod,
                source: paymentTokenId,
                totalPrice: originalServicePrice,
                extensionDays: props.extensionDays,
            })
            .then((response: any) => {
                if (response?.response?.status == 402) {
                    createNotification(
                        response.response.data?.title ??
                            'An unexpected error occured. Please try again later',
                        'error'
                    );
                    setIsPaymentInProcess(false);
                    props.setIsVisible(false);
                } else {
                    handlePricingUpdatedError(response);
                    setTimeout(() => {
                        setIsPaymentInProcess(false);
                        props.paymentSuccess(response.data);
                    }, 3000);
                }
            })
            .catch((err: any) => console.log(err));
    };

    const handlePricingUpdatedError = (response: any) => {
        if (response.response?.status === 422) {
            createNotification(
                'Fee has been changed, please try again',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Price changed');
        }

        if (response.response?.status === 500) {
            createNotification(
                response.response.data?.title ??
                    'An unexpected error occured. Please try again later',
                'error'
            );
            setIsPaymentInProcess(false);
            props.setIsVisible(false);
            throw new Error('Bad request');
        }
    };

    return (
        <div className="late-fee-payment-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={priceList}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                chargeRequest={payLateFee}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                isAuthorizationRequired={false}
            />
        </div>
    );
};

export default LateFeePaymentComponent;
